import styled, { css } from 'styled-components';

import { getOptionCardStyle } from '@/components/AdvertisePayments/components/Options/Options.helpers';
import { Typography } from '@/components/ui/Typography/Typography.styles';
import { CONTAINER_MAX } from '@/constants/breakpoints';
import { flexCenterAligned, flexColumn } from '@/styles/common';

export const OptionsSection = styled.div`
  width: 100%;
`;

export const OptionsCardRow = styled.div<{ $isPublicationPayment: boolean }>`
  border-radius: 28px;
  display: flex;
  gap: 20px;

  ${({ $isPublicationPayment }) =>
    !$isPublicationPayment &&
    css`
      padding: 30px;
      align-items: center;
      background: var(--bg-main);
    `};

  @media (max-width: ${CONTAINER_MAX.tablet}) {
    padding-block: 20px;
    ${({ $isPublicationPayment }) =>
      $isPublicationPayment &&
      css`
        ${flexColumn}
      `}
  }

  @media (max-width: ${CONTAINER_MAX.mobileL}) {
    padding-inline: 20px;
  }
`;

export const OptionCardIcon = styled.div<{
  $isActive: boolean;
  $cardId: string;
  $isDark: boolean;
}>`
  ${flexCenterAligned};
  border-radius: 50px;
  width: 30px;
  height: 30px;
  background: ${({ $isActive, $cardId, $isDark }) =>
    $isActive
      ? getOptionCardStyle($isActive, $isDark, $cardId).iconBackground
      : 'var(--border-secondary)'};
  margin-block-end: 20px;

  @media (max-width: ${CONTAINER_MAX.tablet}) {
    margin-block-end: 15px;
  }
`;

export const OptionCard = styled.div<{
  $isActive: boolean;
  $isDark: boolean;
  $isCardId: string;
}>`
  width: 100%;
  max-width: 220px;
  height: 157px;
  ${flexColumn};
  align-items: center;
  justify-content: center;

  ${({ $isActive, $isDark, $isCardId }) => {
    const cardStyles = getOptionCardStyle($isActive, $isDark, $isCardId);
    return css`
      background: ${cardStyles.background};
      border: 1.5px solid ${cardStyles.borderColor};
    `;
  }}

  border-radius: 21px;
  cursor: pointer;

  transition:
    background 500ms ease-out,
    border-color 500ms ease-in-out;

  @media (min-width: ${CONTAINER_MAX.tablet}) {
    ${({ $isActive }) =>
      !$isActive &&
      css`
        &:hover {
          background: var(--bg-secondary);
          border: 1px solid var(--textAndLabels-main);
        }

        &:hover ${OptionCardIcon} {
          background: var(--border-secondary);
          transition: background 300ms ease-in-out;
        }
      `}
  }

  @media (max-width: ${CONTAINER_MAX.tablet}) {
    max-width: 100%;
    max-height: 127px;
  }
`;

export const OptionCardTitle = styled(Typography).attrs({
  variant: 'title4'
})`
  color: var(--textAndLabels-main);
  margin-block-end: 10px;
  text-align: center;

  @media (max-width: ${CONTAINER_MAX.tablet}) {
    margin-block-end: 5px;
  }
`;

export const OptionCardSubtitle = styled(Typography).attrs({
  variant: 'body1'
})`
  color: var(--textAndLabels-main);
`;

export const OptionDetailsContent = styled.div`
  ${flexColumn};
  gap: 20px;
`;

export const OptionDetailsCard = styled.div`
  border-radius: 28px;
  padding: 30px;
  width: 100%;
  max-width: 520px;
  min-height: auto;
  background: var(--bg-main);

  @media (max-width: ${CONTAINER_MAX.tablet}) {
    max-width: 100%;
    margin-block-end: 14px;
  }
`;

export const OptionFeatureList = styled.ul`
  ${flexColumn};
  gap: 10px;
`;

export const OptionFeatureItem = styled.li`
  width: 100%;
  display: flex;
  gap: 16px;
  padding-block: 8px;
`;

export const OptionFeatureTextGroup = styled.div`
  width: 100%;
  ${flexColumn};
  gap: 4px;
`;

export const OptionFeatureTitle = styled(Typography).attrs({
  variant: 'title5'
})`
  color: var(--textAndLabels-main);
`;

export const OptionFeatureIcon = styled.div`
  width: 20px;
  height: 20px;
  ${flexCenterAligned};
  & * {
    stroke: var(--brand-main);
  }
`;

export const OptionFeatureSubtitle = styled(Typography).attrs({
  variant: 'body3'
})`
  width: 100%;
  color: var(--textAndLabels-tertiary);

  @media (max-width: ${CONTAINER_MAX.tablet}) {
    max-width: 100%;
  }
`;

export const RadioLabel = styled.label`
  width: fit-content;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
`;

export const HiddenRadioInput = styled.input.attrs({ type: 'radio' })`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
`;

export const CheckmarkWrapper = styled.span<{ $isVisible: boolean }>`
  position: absolute;
  ${flexCenterAligned};
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transform: scale(${({ $isVisible }) => ($isVisible ? 1 : 0.5)});
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
`;

export const CustomRadioCircle = styled.span<{ $isChecked: boolean }>`
  width: 20px;
  height: 20px;
  border: 1.5px solid
    ${({ $isChecked }) =>
      $isChecked ? 'var(--textAndLabels-main)' : 'var(--border-secondary)'};
  border-radius: 50%;
  ${flexCenterAligned};
  box-sizing: border-box;
  cursor: pointer;
  transition: border-color 0.2s;
`;

export const InlineText = styled(Typography).attrs({ variant: 'body1' })`
  display: inline-block;
  cursor: pointer;
  color: var(--textAndLabels-main);
`;
